import { createActionCreators, createReducerFunction, ImmerReducer } from 'immer-reducer'
import { Progress } from 'modules/types'
import { arrToDict, getIds } from 'modules/utils/helpers'
import { LIST_PAGE_SIZE } from 'modules/constants'
import { AddError, FetchError, RemoveError, UpdateError } from 'modules/domain/types'
import {
  ContactLifeCycleActivitiesState,
  ContactLifeCycleActivityListRequestFilter,
  ContactLifeCycleActivityListRequestSorting,
  ContactLifeCycleActivity,
  ContactLifeCycleActivityDTO,
} from './types'

const initialState: ContactLifeCycleActivitiesState = {
  items: {},
  meta: {},
  ids: [],

  listFetchProgress: Progress.IDLE,
  listFetchError: null,
  listFetchNextProgress: Progress.IDLE,
  listFetchNextError: null,
  itemFetchProgress: Progress.IDLE,
  itemFetchError: null,
  addProgress: Progress.IDLE,
  addError: null,
  addErrorDetail: '',
  updateProgress: Progress.IDLE,
  updateError: null,
  updateErrorDetail: '',
  removeProgress: Progress.IDLE,
  removeError: null,

  filter: {},
  sorting: {},
  page: 1,
  total: 0,
  pageSize: LIST_PAGE_SIZE,
}

class ContactLifeCycleActivitiesReducer extends ImmerReducer<ContactLifeCycleActivitiesState> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  listRequested(params: {
    filter?: ContactLifeCycleActivityListRequestFilter
    sorting?: ContactLifeCycleActivityListRequestSorting
    page?: number
  }) {
    this.draftState.listFetchProgress = Progress.WORK
    this.draftState.listFetchError = null
    this.draftState.filter = params.filter || this.draftState.filter
    this.draftState.sorting = params.sorting || this.draftState.sorting
    this.draftState.page = typeof params.page === 'undefined' ? this.draftState.page : params.page
  }

  listRequestSucceed(list: ContactLifeCycleActivity[], total: number, page: number) {
    this.draftState.listFetchProgress = Progress.SUCCESS
    this.draftState.total = total
    this.draftState.page = page
    this.draftState.items = arrToDict(list)
    this.draftState.meta = arrToDict(
      list.map((item) => ({
        id: item.id,
        fetchProgress: Progress.SUCCESS,
        fetchError: null,
        removeProgress: Progress.IDLE,
        removeError: null,
        updateProgress: Progress.IDLE,
        updateError: null,
      })),
    )
    this.draftState.ids = getIds(list)
  }

  listRequestFailed(error: FetchError) {
    this.draftState.listFetchProgress = Progress.ERROR
    this.draftState.listFetchError = error
  }

  itemRequested(id: string) {
    this.draftState.itemFetchProgress = Progress.WORK
    const meta = {
      id,
      updateProgress: Progress.IDLE,
      updateError: null,
      removeProgress: Progress.IDLE,
      removeError: null,
    }
    this.draftState.meta[id] = {
      ...meta,
      ...this.draftState.meta[id],
      fetchProgress: Progress.WORK,
      fetchError: null,
    }
  }

  itemRequestSucceed(contactLifeCycleActivity: ContactLifeCycleActivity) {
    this.draftState.itemFetchProgress = Progress.SUCCESS
    this.draftState.items[contactLifeCycleActivity.id] = contactLifeCycleActivity

    if (this.draftState.meta[contactLifeCycleActivity.id]) {
      this.draftState.meta[contactLifeCycleActivity.id].fetchProgress = Progress.SUCCESS
      this.draftState.meta[contactLifeCycleActivity.id].fetchError = null
    } else {
      this.draftState.meta[contactLifeCycleActivity.id] = {
        id: contactLifeCycleActivity.id,
        updateProgress: Progress.IDLE,
        updateError: null,
        removeProgress: Progress.IDLE,
        removeError: null,
        fetchProgress: Progress.SUCCESS,
        fetchError: null,
      }
    }
  }

  itemRequestFailed(id: string, error: FetchError) {
    this.draftState.itemFetchProgress = Progress.ERROR
    this.draftState.meta[id].fetchProgress = Progress.ERROR
    this.draftState.meta[id].fetchError = error
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  addRequested(dto: ContactLifeCycleActivityDTO) {
    this.draftState.addProgress = Progress.WORK
    this.draftState.addError = null
  }

  addSucceed(contactLifeCycleActivity: ContactLifeCycleActivity) {
    this.draftState.addProgress = Progress.SUCCESS
    this.draftState.items[contactLifeCycleActivity.id] = contactLifeCycleActivity
    this.draftState.meta[contactLifeCycleActivity.id] = {
      id: contactLifeCycleActivity.id,
      fetchProgress: Progress.SUCCESS,
      fetchError: null,
      updateProgress: Progress.IDLE,
      updateError: null,
      removeProgress: Progress.IDLE,
      removeError: null,
    }
  }

  addFailed(type: AddError, detail: string | undefined) {
    this.draftState.addProgress = Progress.ERROR
    this.draftState.addError = type

    if (detail) this.draftState.addErrorDetail = detail
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  updateRequested(id: string, contactLifeCycleActivity: Partial<ContactLifeCycleActivityDTO>) {
    this.draftState.updateProgress = Progress.WORK
    this.draftState.meta[id].updateProgress = Progress.WORK
    this.draftState.meta[id].updateError = null
  }

  updateSucceed(contactLifeCycleActivity: ContactLifeCycleActivity) {
    this.draftState.items[contactLifeCycleActivity.id] = contactLifeCycleActivity
    this.draftState.updateProgress = Progress.SUCCESS
    this.draftState.meta[contactLifeCycleActivity.id].updateProgress = Progress.SUCCESS
    this.draftState.meta[contactLifeCycleActivity.id].updateError = null
  }

  updateFailed(id: string, type: UpdateError, detail: string | undefined) {
    this.draftState.meta[id].updateProgress = Progress.ERROR
    this.draftState.meta[id].updateError = type
    this.draftState.updateProgress = Progress.ERROR
    this.draftState.updateError = type

    if (detail) {
      this.draftState.meta[id].updateErrorDetail = detail
      this.draftState.updateErrorDetail = detail
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  removeRequested(id: string) {
    this.draftState.removeProgress = Progress.WORK
    this.draftState.meta[id].removeProgress = Progress.WORK
    this.draftState.meta[id].removeError = null
  }

  removeSucceed(id: string) {
    this.draftState.removeProgress = Progress.SUCCESS
    delete this.draftState.items[id]
    delete this.draftState.meta[id]
    const i = this.draftState.ids.findIndex((item) => item === id)
    if (-1 !== i) {
      const ids = this.draftState.ids
      this.draftState.ids = [...ids.slice(0, i), ...ids.slice(i + 1)]
    }
  }

  removeFailed(id: string, error: RemoveError) {
    this.draftState.removeProgress = Progress.ERROR
    this.draftState.meta[id].removeProgress = Progress.WORK
    this.draftState.meta[id].removeError = error
  }

  filterUpdated(filter: ContactLifeCycleActivityListRequestFilter) {
    this.draftState.filter = filter
    this.draftState.listFetchProgress = Progress.WORK
  }

  sortingUpdated(sorting: ContactLifeCycleActivityListRequestSorting) {
    this.draftState.sorting = sorting
    this.draftState.listFetchProgress = Progress.WORK
  }

  filterHasBeenReset() {
    this.draftState.filter = {}
    this.draftState.listFetchProgress = Progress.WORK
  }

  sortingHasBeenReset() {
    this.draftState.sorting = {}
    this.draftState.listFetchProgress = Progress.WORK
  }

  listRequestedNext(page: number) {
    this.draftState.page = page
    this.draftState.listFetchNextProgress = Progress.WORK
    this.draftState.listFetchError = null
  }

  listRequestNextSucceed(list: ContactLifeCycleActivity[], total: number) {
    this.draftState.listFetchNextProgress = Progress.SUCCESS
    this.draftState.total = total
    this.draftState.items = { ...this.draftState.items, ...arrToDict(list) }
    this.draftState.ids = [...this.draftState.ids, ...getIds(list)]
  }

  listRequestNextFailed(error: FetchError) {
    this.draftState.listFetchNextProgress = Progress.ERROR
    this.draftState.listFetchNextError = error
  }
}

export const ContactLifeCycleActivityActions = createActionCreators(ContactLifeCycleActivitiesReducer)
export default ContactLifeCycleActivityActions
export const reducer = createReducerFunction(ContactLifeCycleActivitiesReducer, initialState)
